<template>
  <div class="ai-assistant-tab" id="ai-assistant-print-area" v-loader.tab="loading">
    <!-- Patient mode always opens on the intro (see togglePatientMode) — a silent muted
         video first, then this welcome/explainer screen, before landing on the real
         patient-facing finding below. Both intro screens replace .body entirely (no
         viewer, no thumbnails, no top-bar) rather than living inside it, since neither
         has anything to do with paging through captures. Only these two cross-fade (see
         .fade-* below) — leaving the video's own black screen should ease into the welcome
         content instead of cutting straight to it; .body's own appearance/disappearance
         (patientModeStep reaching 2, or leaving patient mode entirely) is unrelated and
         stays an instant swap, same as before. -->
    <transition name="fade">
      <ExamAiAssistantPatientIntroVideo
        v-if="isPatientMode && patientModeStep === 0"
        key="video"
        @ended="patientModeStep = 1"
      />
      <ExamAiAssistantPatientIntroWelcome
        v-else-if="isPatientMode && patientModeStep === 1"
        key="welcome"
        :patient-name="patientName"
        @next="patientModeStep = 2"
      />
    </transition>

    <div v-if="!isPatientMode || patientModeStep === 2" class="ai-assistant-tab__body" :class="{
      'ai-assistant-tab__body--patient-mode': isPatientMode,
      'ai-assistant-tab__body--side-by-side': isSideBySideMode,
    }">

      <div class="ai-assistant-tab__panel scrollbar">
        <div class="ai-assistant-tab__scroll">
          <div class="ai-assistant-tab__top-bar">
            <!-- Descriptive standalone twin of the icon-only toggle inside .top-actions below
                 — desktop shows this one (room for the label), mobile swaps to that one (see
                 .patient-mode-btn/.patient-toggle-icon-btn in the stylesheet); never both at
                 once. -->
            <button type="button" class="btn btn-outline-secondary ai-assistant-tab__top-action-btn ai-assistant-tab__patient-mode-btn" @click="togglePatientMode" :title="isPatientMode ? $t('aiAssistant.patientMode.toggleToDoctor') : $t('aiAssistant.patientMode.toggleToPatient')">
              <IconPatientsSimple/>
              {{ isPatientMode ? $t('aiAssistant.patientMode.toggleToDoctor') : $t('aiAssistant.patientMode.toggleToPatient') }}
            </button>

            <div class="btn-group ai-assistant-tab__top-actions" role="group" :aria-label="$t('aiAssistant.topActionsLabel')">
              <button type="button" class="btn btn-outline-secondary ai-assistant-tab__top-action-btn" v-print="printObj" @click="prepareForPrint" :title="$t('aiAssistant.print')">
                <IconPrint/>
              </button>

              <button type="button" class="btn btn-outline-secondary ai-assistant-tab__top-action-btn ai-assistant-tab__patient-toggle-icon-btn" @click="togglePatientMode" :title="isPatientMode ? $t('aiAssistant.patientMode.toggleToDoctor') : $t('aiAssistant.patientMode.toggleToPatient')">
                <IconPatientsSimple/>
              </button>

              <button v-if="canSideBySide && !isPatientMode" type="button" class="btn btn-outline-secondary ai-assistant-tab__top-action-btn" @click="toggleSideBySideMode" :title="$t('aiAssistant.sideBySideMode')">
                <IconCompareEyes/>
              </button>
            </div>

            <div class="ai-assistant-tab__pagination">
              <button type="button" class="ai-assistant-tab__pagination-btn ai-assistant-tab__pagination-btn--prev" :disabled="captures.length < 2" @click="step(-1)" :title="$t('aiAssistant.previous')">
                <IconArrowRight/>
              </button>
              <span class="ai-assistant-tab__pagination-counter">{{ String(activeIndex + 1).padStart(2, '0') }}</span>
              <button type="button" class="ai-assistant-tab__pagination-btn" :disabled="captures.length < 2" @click="step(1)" :title="$t('aiAssistant.next')">
                <IconArrowRight/>
              </button>
            </div>
          </div>

          <div class="ai-assistant-tab__brand">
            <!-- Patient mode's 3 screens are always light-theme regardless of isDarkTheme
                 (see ExamAiAssistantTab.scss) — same reason ExamAiAssistantPatientIntroWelcome
                 hardcodes the plain mark instead of reading isDarkTheme itself. -->
            <img v-if="isDarkTheme && !isPatientMode" src="@/assets/images/ai-assistant/phelcom-ai-assistant-inline-white.png" alt="Phelcom AI Assistant" width="230"/>
            <img v-else src="@/assets/images/ai-assistant/phelcom-ai-assistant-inline.png" alt="Phelcom AI Assistant" width="230"/>
          </div>

          <div class="ai-assistant-tab__print-header">
            <p class="ai-assistant-tab__print-report-label">{{ $t('aiAssistant.printReportLabel') }}</p>
            <strong class="ai-assistant-tab__print-clinic">{{ clinicName }}</strong>
            <div class="ai-assistant-tab__print-patient">
              <div>
                <span>{{ $t('aiAssistant.patientNameLabel') }}</span>
                <strong>{{ patientName }}</strong>
              </div>
              <div>
                <span>{{ $t('aiAssistant.captureDateLabel') }}</span>
                <strong>{{ examDateFormatted }}</strong>
              </div>
            </div>
          </div>

          <div class="ai-assistant-tab__print-images" :class="{'ai-assistant-tab__print-images--solo': !currentCapture.heatmap}" v-if="currentCapture">
            <div class="ai-assistant-tab__print-image">
              <div class="ai-assistant-tab__print-image-frame">
                <img :src="imageUrl(currentCapture.original, dataPath)" :alt="$t('aiAssistant.originalImageAlt')"/>
              </div>
              <div class="ai-assistant-tab__print-caption">
                <span>{{ $t('aiAssistant.imageLabel') }}</span>
                <img class="ai-assistant-tab__eyer2-mark" src="@/assets/images/ai-assistant/logo_eyer_2_color.png" alt="Eyer2"/>
              </div>
            </div>
            <!-- No heatmap: the AI pipeline skips generating one when it finds no
                 alteration to highlight (see buildCaptures) — nothing to print here. -->
            <div v-if="currentCapture.heatmap" class="ai-assistant-tab__print-image">
              <div class="ai-assistant-tab__print-image-frame">
                <img :src="imageUrl(currentCapture.heatmap, dataPath)" :alt="$t('aiAssistant.heatmapImageAlt')"/>
              </div>
              <div class="ai-assistant-tab__print-caption">
                <img class="ai-assistant-tab__eyermaps-logo" src="@/assets/images/ai-assistant/eyermaps-avatar-color.png" alt="EyerMaps — The power of AI"/>
              </div>
            </div>
          </div>

          <div v-if="activeFinding && !isPatientMode" class="ai-assistant-tab__result" :class="`ai-assistant-tab__result--${activeFinding.tone}`">
            <p class="ai-assistant-tab__eyebrow">{{ $t('aiAssistant.resultEyebrow') }}</p>

            <div class="ai-assistant-tab__title-row">
              <span class="ai-assistant-tab__status-icon">
                <IconCheckBig v-if="activeFinding.tone === 'success'"/>
                <IconAlertSimple v-else/>
              </span>
              <h2 class="ai-assistant-tab__title">{{ activeFinding.title }}</h2>
            </div>

            <p class="ai-assistant-tab__summary" v-html="activeFinding.summary"></p>

            <div v-if="activeFinding.classes && activeFinding.classes.length > 1" class="ai-assistant-tab__classes">
              <div v-for="klass in activeFinding.classes" :key="klass.number" class="ai-assistant-tab__class-card">
                <span class="ai-assistant-tab__class-number">{{ $t('aiAssistant.classLabel', { number: klass.number }) }}</span>
                <strong class="ai-assistant-tab__class-title">{{ klass.title }}</strong>
                <span class="ai-assistant-tab__class-description">{{ klass.description }}</span>
              </div>
            </div>

            <template v-if="activeFinding.tags.length">
              <h3 v-if="activeFinding.tagsTitle" class="ai-assistant-tab__subtitle">{{ activeFinding.tagsTitle }}</h3>
              <div class="ai-assistant-tab__tags">
                <span v-for="tag in activeFinding.tags" :key="tag" class="ai-assistant-tab__tag">{{ tag }}</span>
              </div>
            </template>

            <p class="ai-assistant-tab__detail" v-html="activeFinding.detailText"></p>

            <template v-if="activeFinding.doctorInterpretation">
              <h3 class="ai-assistant-tab__subtitle">{{ $t('aiAssistant.doctorInterpretationTitle') }}</h3>
              <p class="ai-assistant-tab__detail" v-html="activeFinding.doctorInterpretation"></p>
            </template>
          </div>

          <!-- Real per-finding patient-mode copy: activeFinding.patient.* comes from
               aiAssistant.findings.<key>.patient in the locale files (see buildFinding /
               buildPatientContent). Only meansTitle/doesNotTitle/possibilityTitle/
               possibilityDisclaimer are fixed/shared across every finding. Wrapped in its
               own flex:1 centering box (same technique as the intro's welcome screen) so
               the sticky .top-bar above stays pinned to the top while this content is
               centered in whatever space is left, instead of the two competing for the
               same justify-content on .scroll. -->
          <div v-if="activeFinding && isPatientMode" class="ai-assistant-tab__patient-result-center">
            <div class="ai-assistant-tab__patient-result" :class="`ai-assistant-tab__result--${activeFinding.tone}`">
              <p class="ai-assistant-tab__patient-eyebrow">{{ activeFinding.title }}</p>
              <h2 class="ai-assistant-tab__patient-heading" v-html="activeFinding.patient.heading"></h2>

              <h3 class="ai-assistant-tab__patient-subtitle">{{ activeFinding.patient.regionTitle }}</h3>
              <p class="ai-assistant-tab__patient-detail" v-html="activeFinding.patient.regionText1"></p>
              <p class="ai-assistant-tab__patient-detail" v-html="activeFinding.patient.regionText2"></p>

              <div class="ai-assistant-tab__patient-cards">
                <div class="ai-assistant-tab__patient-card ai-assistant-tab__patient-card--positive">
                  <h4>{{ $t('aiAssistant.patientMode.meansTitle') }}</h4>
                  <ul>
                    <li v-for="(item, i) in activeFinding.patient.meansItems" :key="i" v-html="item"></li>
                  </ul>
                </div>
                <div class="ai-assistant-tab__patient-card">
                  <h4>{{ $t('aiAssistant.patientMode.doesNotTitle') }}</h4>
                  <ul>
                    <li v-for="(item, i) in activeFinding.patient.doesNotItems" :key="i" v-html="item"></li>
                  </ul>
                </div>
              </div>

              <template v-if="activeFinding.tags.length">
                <h3 class="ai-assistant-tab__patient-subtitle">{{ $t('aiAssistant.patientMode.possibilityTitle') }}</h3>
                <div class="ai-assistant-tab__tags">
                  <span v-for="tag in activeFinding.tags" :key="tag" class="ai-assistant-tab__tag">{{ tag }}</span>
                </div>
                <!-- <p class="ai-assistant-tab__patient-disclaimer">{{ $t('aiAssistant.patientMode.possibilityDisclaimer') }}</p> -->
              </template>
            </div>
          </div>


        </div>

        <div class="ai-assistant-tab__tech">
          <button type="button" class="ai-assistant-tab__tech-toggle" @click="techInfoOpen = !techInfoOpen">
            <span>{{ $t('aiAssistant.technology.toggle') }}</span>
            <IconMinus v-if="techInfoOpen"/>
            <IconPlus v-else/>
          </button>

          <div class="ai-assistant-tab__tech-content" :class="{'ai-assistant-tab__tech-content--open': techInfoOpen}">
            <h4 class="ai-assistant-tab__tech-subtitle">{{ $t('aiAssistant.technology.title') }}</h4>
            <p class="no-margin" v-html="$t('aiAssistant.technology.description')"></p>

            <div class="ai-assistant-tab__tech-badges">
              <span class="ai-assistant-tab__tech-badge">
                <IconCheckBig/>
                {{ $t('aiAssistant.technology.badgeAnvisa') }}
              </span>
              <span class="ai-assistant-tab__tech-badge">
                <IconCheckBig/>
                {{ $t('aiAssistant.technology.badgeExplainable') }}
              </span>
              <span class="ai-assistant-tab__tech-badge">
                <IconCheckBig/>
                {{ $t('aiAssistant.technology.badgeClinicalSupport') }}
              </span>
            </div>

            <h4 class="ai-assistant-tab__tech-subtitle">{{ $t('aiAssistant.technology.clinicalPerformance') }}</h4>

            <div class="ai-assistant-tab__tech-metrics">
              <div class="ai-assistant-tab__tech-metric-card">
                <strong>{{ $t('aiAssistant.technology.diabeticRetinopathy') }}</strong>
                <div class="ai-assistant-tab__tech-metric-row">
                  <div>
                    <span>{{ $t('aiAssistant.technology.sensitivity') }}</span>
                    <strong>{{ metrics.diabeticRetinopathy.sensitivity }}</strong>
                  </div>
                  <div>
                    <span>{{ $t('aiAssistant.technology.specificity') }}</span>
                    <strong>{{ metrics.diabeticRetinopathy.specificity }}</strong>
                  </div>
                </div>
              </div>

              <div class="ai-assistant-tab__tech-metric-card">
                <strong>{{ $t('aiAssistant.technology.retinalAlterations') }}</strong>
                <div class="ai-assistant-tab__tech-metric-row">
                  <div>
                    <span>{{ $t('aiAssistant.technology.sensitivity') }}</span>
                    <strong>{{ metrics.generalAlterations.sensitivity }}</strong>
                  </div>
                  <div>
                    <span>{{ $t('aiAssistant.technology.specificity') }}</span>
                    <strong>{{ metrics.generalAlterations.specificity }}</strong>
                  </div>
                </div>
              </div>
            </div>

            <p class="no-margin" v-html="$t('aiAssistant.technology.metricsSummary', {
              drSensitivity: metrics.diabeticRetinopathy.sensitivity,
              drSpecificity: metrics.diabeticRetinopathy.specificity,
              gaSensitivity: metrics.generalAlterations.sensitivity,
              gaSpecificity: metrics.generalAlterations.specificity,
            })"></p>

            <p class="ai-assistant-tab__tech-note">
              <strong>{{ $t('aiAssistant.technology.clinicalNoteLabel') }}</strong>
              {{ $t('aiAssistant.technology.clinicalNoteText', { value: metrics.opticNerve.sensitivity }) }}
            </p>

            <p class="ai-assistant-tab__tech-note">
              <strong>{{ $t('aiAssistant.technology.importantLabel') }}</strong>
              {{ $t('aiAssistant.technology.importantText') }}
            </p>
          </div>
        </div>

        <div class="ai-assistant-tab__print-footer">
          <img src="@/assets/images/ai-assistant/phelcom-ai-assistant-inline.png" alt="Phelcom AI Assistant" width="160"/>
        </div>
      </div>

      <div class="ai-assistant-tab__viewer" ref="viewer">
        <template v-if="isSideBySideMode">
          <div class="ai-assistant-tab__side-by-side-controls">
            <button type="button" class="btn btn-outline-secondary ai-assistant-tab__exit-mode-btn" @click="toggleSideBySideMode">
              <IconCompareEyes/>
              {{ $t('aiAssistant.exitMode') }}
            </button>
            <span class="ai-assistant-tab__side-by-side-label">{{ $t('aiAssistant.sideBySideMode') }}</span>
          </div>

          <div class="ai-assistant-tab__eyes">
            <div class="ai-assistant-tab__eye-panel">
              <div class="ai-assistant-tab__frame">
                <template v-if="currentLeftEyeCapture">
                  <template v-if="currentLeftEyeCapture.heatmap">
                    <div v-if="!isLoaded(`slider-${currentLeftEyeCapture.id}`)" class="ai-assistant-tab__slider-loading loading-eye" @vue:mounted="onSliderMounted($event, `slider-${currentLeftEyeCapture.id}`)"></div>
                    <!-- img-comparison-slider is a native custom element (see vue.config.js
                         isCustomElement) — these are real DOM slot attributes, not Vue's own
                         slot mechanism. -->
                    <!-- eslint-disable vue/no-deprecated-slot-attribute -->
                    <img-comparison-slider class="ai-assistant-tab__slider" value="50">
                      <img slot="first" :src="imageUrl(currentLeftEyeCapture.original, dataPath)" :alt="$t('aiAssistant.originalImageAlt')"/>
                      <img slot="second" :src="imageUrl(currentLeftEyeCapture.heatmap, dataPath)" :alt="$t('aiAssistant.heatmapImageAlt')"/>
                      <div slot="handle" class="ai-assistant-tab__slider-handle">
                        <IconSlide/>
                      </div>
                    </img-comparison-slider>
                    <!-- eslint-enable vue/no-deprecated-slot-attribute -->
                  </template>
                  <div v-else class="ai-assistant-tab__frame-media" :class="{'loading-eye': !isLoaded(`solo-${currentLeftEyeCapture.id}`)}">
                    <img class="ai-assistant-tab__solo-image" :src="imageUrl(currentLeftEyeCapture.original, dataPath)" :alt="$t('aiAssistant.originalImageAlt')" @load="markLoaded(`solo-${currentLeftEyeCapture.id}`)" @vue:mounted="onImgMounted($event, `solo-${currentLeftEyeCapture.id}`)"/>
                  </div>

                  <div class="ai-assistant-tab__label ai-assistant-tab__label--left">
                    <span>{{ $t('aiAssistant.imageLabel') }}</span>
                    <img class="ai-assistant-tab__eyer2-mark" src="@/assets/images/phelcom/eyer2_logo.svg" alt="Eyer2"/>
                  </div>

                  <div class="ai-assistant-tab__label ai-assistant-tab__label--right">
                    <img class="ai-assistant-tab__eyermaps-logo" src="@/assets/images/ai-assistant/eyermaps.png" alt="EyerMaps — The power of AI"/>
                  </div>
                </template>
                <BaseNoResults v-else :title="$t('aiAssistant.noImagesTitle')" :text="$t('aiAssistant.noImagesText')"/>
              </div>

              <div v-if="leftEyeCaptures.length > 1" class="ai-assistant-tab__thumbs scrollbar" @wheel="onThumbsWheel" @mousedown="onThumbsDragStart">
                <button v-for="(capture, index) in leftEyeCaptures" :key="capture.id" type="button"
                        class="ai-assistant-tab__thumb" :class="{'ai-assistant-tab__thumb--active': index === sideBySideActiveIndexLeft, 'loading-eye': !isLoaded(`thumb-${capture.id}`)}"
                        @click="sideBySideActiveIndexLeft = index">
                  <img :src="imageUrl(capture.original, thumbDataPath)" :alt="$t('aiAssistant.captureAlt', { n: index + 1 })" @load="markLoaded(`thumb-${capture.id}`)" @vue:mounted="onImgMounted($event, `thumb-${capture.id}`)"/>
                </button>
              </div>
            </div>

            <div class="ai-assistant-tab__eye-panel">
              <div class="ai-assistant-tab__frame">
                <template v-if="currentRightEyeCapture">
                  <template v-if="currentRightEyeCapture.heatmap">
                    <div v-if="!isLoaded(`slider-${currentRightEyeCapture.id}`)" class="ai-assistant-tab__slider-loading loading-eye" @vue:mounted="onSliderMounted($event, `slider-${currentRightEyeCapture.id}`)"></div>
                    <!-- img-comparison-slider is a native custom element (see vue.config.js
                         isCustomElement) — these are real DOM slot attributes, not Vue's own
                         slot mechanism. -->
                    <!-- eslint-disable vue/no-deprecated-slot-attribute -->
                    <img-comparison-slider class="ai-assistant-tab__slider" value="50">
                      <img slot="first" :src="imageUrl(currentRightEyeCapture.original, dataPath)" :alt="$t('aiAssistant.originalImageAlt')"/>
                      <img slot="second" :src="imageUrl(currentRightEyeCapture.heatmap, dataPath)" :alt="$t('aiAssistant.heatmapImageAlt')"/>
                      <div slot="handle" class="ai-assistant-tab__slider-handle">
                        <IconSlide/>
                      </div>
                    </img-comparison-slider>
                    <!-- eslint-enable vue/no-deprecated-slot-attribute -->
                  </template>
                  <div v-else class="ai-assistant-tab__frame-media" :class="{'loading-eye': !isLoaded(`solo-${currentRightEyeCapture.id}`)}">
                    <img class="ai-assistant-tab__solo-image" :src="imageUrl(currentRightEyeCapture.original, dataPath)" :alt="$t('aiAssistant.originalImageAlt')" @load="markLoaded(`solo-${currentRightEyeCapture.id}`)" @vue:mounted="onImgMounted($event, `solo-${currentRightEyeCapture.id}`)"/>
                  </div>

                  <div class="ai-assistant-tab__label ai-assistant-tab__label--left">
                    <span>{{ $t('aiAssistant.imageLabel') }}</span>
                    <img class="ai-assistant-tab__eyer2-mark" src="@/assets/images/phelcom/eyer2_logo.svg" alt="Eyer2"/>
                  </div>

                  <div class="ai-assistant-tab__label ai-assistant-tab__label--right">
                    <img class="ai-assistant-tab__eyermaps-logo" src="@/assets/images/ai-assistant/eyermaps.png" alt="EyerMaps — The power of AI"/>
                  </div>
                </template>
                <BaseNoResults v-else :title="$t('aiAssistant.noImagesTitle')" :text="$t('aiAssistant.noImagesText')"/>
              </div>

              <div v-if="rightEyeCaptures.length > 1" class="ai-assistant-tab__thumbs scrollbar" @wheel="onThumbsWheel" @mousedown="onThumbsDragStart">
                <button v-for="(capture, index) in rightEyeCaptures" :key="capture.id" type="button"
                        class="ai-assistant-tab__thumb" :class="{'ai-assistant-tab__thumb--active': index === sideBySideActiveIndexRight, 'loading-eye': !isLoaded(`thumb-${capture.id}`)}"
                        @click="sideBySideActiveIndexRight = index">
                  <img :src="imageUrl(capture.original, thumbDataPath)" :alt="$t('aiAssistant.captureAlt', { n: index + 1 })" @load="markLoaded(`thumb-${capture.id}`)" @vue:mounted="onImgMounted($event, `thumb-${capture.id}`)"/>
                </button>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="ai-assistant-tab__frame">
            <template v-if="currentCapture">
              <div v-if="!isPatientMode" class="btn-group ai-assistant-tab__viewer-controls" role="group" :aria-label="$t('aiAssistant.viewerControlsLabel')">
                <button type="button" class="btn btn-outline-secondary ai-assistant-tab__fullscreen-btn" :class="{'ai-assistant-tab__fullscreen-btn--active': isFullscreen}" @click="toggleFullscreen" :title="isFullscreen ? $t('aiAssistant.minimize') : $t('aiAssistant.maximize')">
                  <IconCollapse v-if="isFullscreen"/>
                  <IconExpand v-else/>
                  <span v-if="isFullscreen">{{ $t('aiAssistant.minimize') }}</span>
                </button>
              </div>

              <template v-if="currentCapture.heatmap">
                <div v-if="!isLoaded(`slider-${currentCapture.id}`)" class="ai-assistant-tab__slider-loading loading-eye" @vue:mounted="onSliderMounted($event, `slider-${currentCapture.id}`)"></div>
                <!-- img-comparison-slider is a native custom element (see vue.config.js
                     isCustomElement) — these are real DOM slot attributes, not Vue's own slot
                     mechanism. -->
                <!-- eslint-disable vue/no-deprecated-slot-attribute -->
                <img-comparison-slider class="ai-assistant-tab__slider" value="50">
                  <img slot="first" :src="imageUrl(currentCapture.original, dataPath)" :alt="$t('aiAssistant.originalImageAlt')"/>
                  <img slot="second" :src="imageUrl(currentCapture.heatmap, dataPath)" :alt="$t('aiAssistant.heatmapImageAlt')"/>
                  <div slot="handle" class="ai-assistant-tab__slider-handle">
                    <IconSlide/>
                  </div>
                </img-comparison-slider>
                <!-- eslint-enable vue/no-deprecated-slot-attribute -->
              </template>
              <!-- No heatmap: the AI pipeline skips generating one when it finds no
                   alteration to highlight (see buildCaptures) — just show the photo. -->
              <div v-else class="ai-assistant-tab__frame-media" :class="{'loading-eye': !isLoaded(`solo-${currentCapture.id}`)}">
                <img class="ai-assistant-tab__solo-image" :src="imageUrl(currentCapture.original, dataPath)" :alt="$t('aiAssistant.originalImageAlt')" @load="markLoaded(`solo-${currentCapture.id}`)" @vue:mounted="onImgMounted($event, `solo-${currentCapture.id}`)"/>
              </div>

              <div class="ai-assistant-tab__label ai-assistant-tab__label--left">
                <span>{{ $t('aiAssistant.imageLabel') }}</span>
                <img class="ai-assistant-tab__eyer2-mark" src="@/assets/images/phelcom/eyer2_logo.svg" alt="Eyer2"/>
              </div>

              <div class="ai-assistant-tab__label ai-assistant-tab__label--right">
                <img class="ai-assistant-tab__eyermaps-logo" src="@/assets/images/ai-assistant/eyermaps.png" alt="EyerMaps — The power of AI"/>
              </div>
            </template>

            <BaseNoResults v-else-if="fetched" :title="$t('aiAssistant.noImagesTitle')" :text="$t('aiAssistant.noImagesText')"/>
          </div>

          <div v-if="captures.length > 1" class="ai-assistant-tab__thumbs scrollbar" @wheel="onThumbsWheel" @mousedown="onThumbsDragStart">
            <button v-for="(capture, index) in captures" :key="capture.id" type="button"
                    class="ai-assistant-tab__thumb" :class="{'ai-assistant-tab__thumb--active': index === activeIndex, 'loading-eye': !isLoaded(`thumb-${capture.id}`)}"
                    @click="activeIndex = index">
              <img :src="imageUrl(capture.original, thumbDataPath)" :alt="$t('aiAssistant.captureAlt', { n: index + 1 })" @load="markLoaded(`thumb-${capture.id}`)" @vue:mounted="onImgMounted($event, `thumb-${capture.id}`)"/>
            </button>
          </div>
        </template>
      </div>

    </div>
  </div>
</template>

<style lang="scss" scoped src="./ExamAiAssistantTab.scss"></style>

<style lang="scss">
:root.dark-theme {
  .ai-assistant-tab__result--warning {
    --tone-border:                #5A4A29;
  }

  .ai-assistant-tab__class-card,
  .ai-assistant-tab__tag,
  .ai-assistant-tab__nav-btn {
    background:                   var(--Shades-W);
  }

  .ai-assistant-tab__class-description {
    color:                         var(--Shades-30);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition:                     opacity .4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity:                        0;
}

.fade-leave-active {
  position:                       absolute;
  inset:                          0;
}
</style>

<script type="text/javascript" src="./ExamAiAssistantTab.js"></script>
